<template>
  <section class="ma-4 ma-md-8">
    <t-page-title title="Produtores | Gerenciar" :on-add="() => $router.push({ name: 'create-producer' })" />
    <v-row justify="center" align="center">
      <v-col>
        <data-table
          :headers="headers"
          :provider="fetch"
          :filters="filters"
          :resource="resource"
          :actions="actions"
        >
          <template #[`custom.actions`]="{ item }">
            <v-btn
              v-if="can('restore', 'producers') && item.status !== 'active'"
              density="compact"
              min-height="32"
              min-width="32"
              elevation="1"
              class="mr-2 pa-2"
              icon
              @click="restore(item)"
            >
              <v-icon size="x-small" icon="fas fa-trash-arrow-up" color="success" />
            </v-btn>
          </template>
          <template #[`item.id`]="{ item }">
            <span> {{ item.id.split('-')[0] }}</span>
          </template>
          <template #[`item.trade_name`]="{ item }">
            <div class="d-flex flex-column">
              <span>{{ item.data.trade_name }}</span>
              <span class="text-caption"> {{ item.data.company_name }}</span>
            </div>
          </template>
          <template #[`item.created_at`]="{ item }">
            <span> {{ new Date(item.created_at).toLocaleDateString("pt-BR", { year: "numeric", month: "2-digit", day: "2-digit" }) }}</span>
          </template>
          <template #[`item.status`]="{ item }">
            <span> {{ item.status === 'active' ? 'Ativo' : 'Inativo' }}</span>
          </template>
        </data-table>
      </v-col>
    </v-row>
  </section>
</template>

<script lang="ts" setup>

import { ref } from 'vue'
import DataTable from '@/components/datatable/DataTable.vue'
import TPageTitle from '@/components/TPageTitle.vue'
import type { DataTableHeader, DefaultDataTableActions } from '@/types/DataTable'
import ProducerService from '@/services/ProducerService'
import type Producer from '@/types/Producer'
import { useRouter } from 'vue-router'
import { useSnackBar } from '@/stores/snackbar'
import { useLoader } from '@/stores/loader'
import { Resources } from '@/plugins/permissions/typing'
import { useTextFilter } from '@/helpers/reports/textFilter'
import { useSelectFilter } from '@/helpers/reports/selectFilter'
import { useAbility } from '@casl/vue'

const resource: Resources = 'producers'

const router = useRouter()
const snackbar = useSnackBar()
const loader = useLoader()
const { can } = useAbility()

const headers = ref([
  { title: 'ID', value: 'id', key: 'id', sortable: false },
  { title: 'Nome de Exibição', value: 'data.display_name', key: 'data.display_name', sortable: false },
  { title: 'Nome / Razão Social', value: 'data.trade_name', key: 'trade_name', sortable: false },
  { title: 'Criado em', value: 'created_at', key: 'created_at', sortable: false },
  { title: 'Status', value: 'status', key: 'status', sortable: false },
  { title: 'Ações', value: 'actions', key: 'actions', sortable: false, align: 'center' },
] as DataTableHeader[])

const filters = ref([
  useTextFilter({
    field: 'name',
    label: 'Nome',
    icon: 'fa-regular fa-signature',
    required: false,
  }),
  useSelectFilter({
    field: 'status',
    label: 'Status',
    required: false,
    icon: '',
    items: [
      { title: 'Todos', value: '' },
      { title: 'Ativo', value: 'true' },
      { title: 'Inativo', value: 'false' },
    ],
  }),
])

const actions = ref({
  read: {
    callback: (item: Producer) => router.push({ name: 'read-producer', params: { id: item.id } }),
    icon: 'fa-solid fa-magnifying-glass',
    color: 'primary',
  },
  update: {
    callback: (item: Producer) => router.push({ name: 'edit-producer', params: { id: item.id } }),
    icon: 'fa-solid fa-pen',
    color: 'info',
  },
  destroy: {
    callback: (item: Producer) => destroy(item),
    condition: (item: Producer) => item.status === 'active',
    icon: 'fa-solid fa-trash',
    color: 'error',
  },
} as DefaultDataTableActions)

async function fetch(page: number, filters: string, per_page?: number) {
  try {
    const response = await (new ProducerService()).index(page, filters.replace('undefined', ''), per_page)
    return response
  } catch (error) {
    snackbar.show(
      'Não foi possível carregar os produtores.',
      'error',
    )

    throw error
  }
}

async function destroy(item: Producer) {
  try {
    loader.increment()
    await (new ProducerService()).destroy(item.id)

    item.status = 'inactive'

    snackbar.show(
      'Produtor excluído com sucesso.',
      'success',
    )
  } finally {
    loader.decrement()
  }
}

async function restore(item: Producer) {
  try {
    loader.increment()

    await (new ProducerService()).restore(item.id)

    item.status = 'active'

    snackbar.show(
      'Produtor restaurado com sucesso.',
      'success',
    )
  } finally {
    loader.decrement()
  }
}

</script>
